import { useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import EmployeePerformanceConfigModal from './EmployeePerformanceConfigModal'
import {
    EMPLOYEE_COLUMNS,
    getEmployeeColumn,
    resolveEmployeeSettings,
    type EmployeePerformanceSettings,
} from '../../lib/employeePerformance'

// ── Employee Performance widget: the edit-mode ⚙ popover content ────────
//
// Both grids render this inside their existing .widget-settings-popover. The popover is too small
// for a column editor, so it carries a one-line summary, the button that opens the editor, and the
// two switches people flip most often. onChange receives a settings patch for the widget.

export interface EmployeePerformanceSettingsControlProps {
    settings: EmployeePerformanceSettings | undefined
    onChange: (patch: EmployeePerformanceSettings) => void
}

export default function EmployeePerformanceSettingsControl({ settings, onChange }: EmployeePerformanceSettingsControlProps) {
    const [editorOpen, setEditorOpen] = useState(false)
    const openerRef = useRef<HTMLButtonElement>(null)
    const resolved = useMemo(() => resolveEmployeeSettings(settings), [settings])

    const shown = resolved.columns.filter(c => c.visible).length
    const sortDef = getEmployeeColumn(resolved.sortKey)
    const sortName = sortDef.menuLabel({ includeReturns: true })
    const arrow = resolved.sortDir === 'desc' ? '↓' : '↑'
    const dirWords = sortDef.numeric
        ? (resolved.sortDir === 'desc' ? 'highest first' : 'lowest first')
        : (resolved.sortDir === 'asc' ? 'A to Z' : 'Z to A')

    const closeEditor = () => {
        setEditorOpen(false)
        openerRef.current?.focus()   // back to where the keyboard user left off
    }

    return (
        <div className="emp-perf-settings">
            <div className="emp-perf-settings-summary">
                {shown} of {EMPLOYEE_COLUMNS.length} columns · sorted by {sortName}{' '}
                <span aria-hidden="true">{arrow}</span>
                <span className="sr-only">, {dirWords}</span>
            </div>
            <button
                ref={openerRef}
                type="button"
                className="btn btn-secondary emp-perf-settings-open"
                aria-haspopup="dialog"
                onClick={() => setEditorOpen(true)}
            >
                Configure columns…
            </button>
            <div className="emp-perf-settings-options">
                <label>
                    <input type="checkbox" checked={resolved.showTotals}
                        onChange={e => onChange({ employeeShowTotals: e.target.checked })} />
                    Show totals row
                </label>
                <label>
                    <input type="checkbox" checked={resolved.showBars}
                        onChange={e => onChange({ employeeShowBars: e.target.checked })} />
                    Show sales bars
                </label>
            </div>
            {editorOpen && createPortal(
                <EmployeePerformanceConfigModal settings={settings} onApply={onChange} onClose={closeEditor} />,
                document.body,
            )}
        </div>
    )
}
